// localStorage cache layer. Every data key is scoped to the signed-in user's
// id so cached pay data can never leak between accounts on a shared device —
// and unsynced offline records can never "heal" into the wrong account.
// The theme key stays device-global: index.html's pre-paint script reads it
// before anyone is signed in.
import { pb, reportStorageWarn } from "./pb";

const STORAGE_KEY    = "ot_ledger_v9";
const EMPLOYEE_KEY   = "ot_employee_v1";
const THEME_KEY      = "ot_theme_v1";
const LAST_ENTRY_KEY = "ot_last_entry_v1";
const LAST_EXP_KEY   = "ot_last_exp_v1";
const NOTES_KEY      = "ot_daynotes_v1";
const EXP_KEY        = "ot_expenses_v1";
const TIMEACCT_KEY   = "ot_timeaccts_v1";
const PENDING_DEL_KEY = "ot_pending_deletes_v1";
const ESS_COMMENTS_KEY = "ot_ess_comments_v1";

const userKey = (base) => `${base}:${pb.authStore.model?.id || "anon"}`;
function read(base, fallback) {
  try { return JSON.parse(localStorage.getItem(userKey(base))) ?? fallback; }
  catch { return fallback; }
}
function write(base, val) {
  try { localStorage.setItem(userKey(base), JSON.stringify(val)); return true; }
  catch (e) { reportStorageWarn(); console.warn("local cache save failed:", e); return false; }
}

// One-time cleanup of the old account-agnostic keys. They may hold a previous
// user's data, so they're removed rather than migrated (PocketBase is the
// source of truth; the per-user cache refills on next load).
export function purgeLegacyKeys() {
  [STORAGE_KEY, EMPLOYEE_KEY, LAST_ENTRY_KEY, LAST_EXP_KEY, NOTES_KEY, EXP_KEY]
    .forEach(k => { try { localStorage.removeItem(k); } catch {} });
}

export function loadCurrent() { return read(STORAGE_KEY, null); }
export function saveCurrent(d) { write(STORAGE_KEY, d); }
export function loadTheme() { try { return localStorage.getItem(THEME_KEY) || "system"; } catch { return "system"; } }
export function saveTheme(t) { try { localStorage.setItem(THEME_KEY, t); } catch {} }
export function loadLastEntry() { return read(LAST_ENTRY_KEY, null); }
export function saveLastEntry(e) { write(LAST_ENTRY_KEY, e); }
export function loadLastExp() { return read(LAST_EXP_KEY, null); }
export function saveLastExp(e) { write(LAST_EXP_KEY, e); }
export function loadEmployee() { return read(EMPLOYEE_KEY, { name: "", employeeId: "", department: "" }); }
export function saveEmployee(e) { write(EMPLOYEE_KEY, e); }
export function loadNotes() { return read(NOTES_KEY, {}); }
export function saveNotes(n) { write(NOTES_KEY, n); }
export function loadExpenses() { return read(EXP_KEY, {}); }
// Receipts are big base64 blobs; a full expense history can blow the ~5MB
// localStorage quota. On failure, retry with SYNCED receipts stripped — those
// are safe in PocketBase. receiptOmitted marks the gap so the UI can say
// "receipt synced" and pbSaveExpense knows not to wipe the remote copy.
export function saveExpenses(x) {
  if (write(EXP_KEY, x)) return;
  const slim = {};
  Object.entries(x || {}).forEach(([d, arr]) => {
    slim[d] = (arr || []).map(r => (r.pbId && r.receipt) ? { ...r, receipt: "", receiptOmitted: true } : r);
  });
  write(EXP_KEY, slim);
}
export function loadTimeAccounts() { return read(TIMEACCT_KEY, {}); }
export function saveTimeAccounts(a) { write(TIMEACCT_KEY, a); }

// The ESS "Comments" text per (month, cost centre). Only present once the user
// has edited the box — an absent key means "still showing the generated
// day-by-day draft", so their own wording is what's cached, never the draft.
export function loadEssComments() { return read(ESS_COMMENTS_KEY, {}); }
export function saveEssComments(c) { write(ESS_COMMENTS_KEY, c); }

// Deletes attempted while offline (or otherwise failed to reach PocketBase) —
// pbIds staged here are retried on every sync until the server confirms them,
// so a failed delete can't be silently undone by the next server pull.
export function loadPendingDeletes() { return read(PENDING_DEL_KEY, { entries: [], expenses: [], day_notes: [] }); }
export function savePendingDeletes(p) { write(PENDING_DEL_KEY, p); }
